import { useEffect, useState, type RefObject } from "react";

const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll"] as const;

// Runs `cb` once the page has fully loaded and then either the visitor
// interacts or `delayMs` passes, whichever comes first. Keeps non-essential
// work (analytics, background fetches, image prefetches) off the initial
// load, where it competes with the LCP image and blocks the main thread.
export function afterPageSettles(cb: () => void, delayMs = 3500): () => void {
  if (typeof window === "undefined") return () => {};

  let done = false;
  let timer: number | undefined;

  const cleanup = () => {
    INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, run, true));
    window.removeEventListener("load", onLoad);
    if (timer !== undefined) window.clearTimeout(timer);
  };

  function run() {
    if (done) return;
    done = true;
    cleanup();
    // Safari has no requestIdleCallback.
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => cb(), { timeout: 2000 });
    } else {
      setTimeout(cb, 1);
    }
  }

  function onLoad() {
    timer = window.setTimeout(run, delayMs);
  }

  INTERACTION_EVENTS.forEach((e) =>
    window.addEventListener(e, run, { capture: true, passive: true }),
  );
  if (document.readyState === "complete") onLoad();
  else window.addEventListener("load", onLoad, { once: true });

  return () => {
    done = true;
    cleanup();
  };
}

// One shared "page settled" signal for every component that asks.
let pageSettled = false;
let watching = false;
const settleListeners = new Set<() => void>();

export function usePageSettled(): boolean {
  const [settled, setSettled] = useState(pageSettled);
  useEffect(() => {
    if (pageSettled) {
      setSettled(true);
      return;
    }
    const listener = () => setSettled(true);
    settleListeners.add(listener);
    if (!watching) {
      watching = true;
      afterPageSettles(() => {
        pageSettled = true;
        settleListeners.forEach((l) => l());
        settleListeners.clear();
      });
    }
    return () => {
      settleListeners.delete(listener);
    };
  }, []);
  return settled;
}

// Third-party analytics (GA, Umami, Vercel) are the heaviest scripts left
// (~200-500 ms of main-thread work each), so they wait for the first
// interaction or 12 s — the same rule as Clarity's loader in index.html.
// Visitors who leave within 12 s without touching the page aren't tracked.
export const ANALYTICS_DELAY_MS = 12000;

export function useAnalyticsReady(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => afterPageSettles(() => setReady(true), ANALYTICS_DELAY_MS), []);
  return ready;
}

// True while the element is on (or near) screen. Used to pause auto-rotating
// carousels that would otherwise re-render offscreen every few seconds.
export function useInView<T extends Element>(ref: RefObject<T | null>, rootMargin = "200px"): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      rootMargin,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, rootMargin]);
  return inView;
}

// Auto-rotation should run only once the page has settled and while visible.
export function useAutoplayAllowed<T extends Element>(ref: RefObject<T | null>): boolean {
  const settled = usePageSettled();
  const inView = useInView(ref);
  return settled && inView;
}
